import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../config/api';
import { FiGlobe, FiSave, FiPlus, FiTrash2, FiClock, FiExternalLink, FiKey, FiDownload } from 'react-icons/fi';
import LoadingButton from '../LoadingButton';
import { SCANNER_PROVIDERS, emptyScanSource } from './scannerUtils';
import { appAlert, appConfirm } from '../../utils/appDialog';

const ScannerConfig = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    service_url: '',
    client_id: '',
    client_secret: '',
    api_secret: '',
    schedule_enabled: false,
    schedule_cron: '0 6 * * *',
    default_job_status: 'active',
    title_positive: '',
    title_negative: 'Intern\nJunior',
  });
  const [secretMeta, setSecretMeta] = useState({
    api_secret_set: false,
    api_secret_preview: '',
    client_secret_set: false,
    client_secret_preview: '',
  });
  const [newSource, setNewSource] = useState(emptyScanSource);

  const { data: settings, isLoading: settingsLoading } = useQuery(
    ['scanner-settings'],
    () => api.get('/scanner/settings').then((r) => r.data)
  );

  const { data: sources = [], isLoading: sourcesLoading } = useQuery(
    ['scanner-sources'],
    () => api.get('/scanner/sources').then((r) => r.data)
  );

  useEffect(() => {
    if (!settings) return;
    setForm((prev) => ({
      ...prev,
      service_url: settings.service_url || '',
      client_id: settings.client_id || '',
      client_secret: '',
      api_secret: '',
      schedule_enabled: settings.schedule_enabled,
      schedule_cron: settings.schedule_cron || prev.schedule_cron,
      default_job_status: settings.default_job_status || 'active',
      title_positive: (settings.title_filter?.positive || []).join('\n'),
      title_negative: (settings.title_filter?.negative || ['Intern', 'Junior']).join('\n'),
    }));
    setSecretMeta({
      api_secret_set: Boolean(settings.api_secret_set),
      api_secret_preview: settings.api_secret_preview || '',
      client_secret_set: Boolean(settings.client_secret_set),
      client_secret_preview: settings.client_secret_preview || '',
    });
  }, [settings]);

  const saveMutation = useMutation(
    (body) => api.put('/scanner/settings', body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['scanner-settings']);
        queryClient.invalidateQueries(['scanner-status']);
        const sync = res.data?.sync;
        if (sync && !sync.ok) {
          void appAlert(`Saved locally, but failed to push to scanner: ${sync.error || 'unreachable'}`);
        } else {
          void appAlert('Scanner configuration saved and pushed to the scanner service.');
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const addSourceMutation = useMutation(
    (body) => api.post('/scanner/sources', body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scanner-sources']);
        queryClient.invalidateQueries(['scanner-status']);
        setNewSource(emptyScanSource);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const seedTechMutation = useMutation(
    () => api.post('/scanner/sources/seed-tech', { enabled: true }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['scanner-sources']);
        queryClient.invalidateQueries(['scanner-status']);
        const { added = 0, skipped = 0, total_seed = 0, sync } = res.data || {};
        const syncNote =
          sync && sync.ok === false
            ? `\nSaved in GoDash, but scanner sync failed: ${sync.data?.error || sync.error || 'unreachable'}`
            : '';
        void appAlert(
          `Starter tech companies loaded.\nAdded: ${added}\nSkipped (already present): ${skipped}\nSeed size: ${total_seed}${syncNote}`
        );
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updateSourceMutation = useMutation(
    ({ id, body }) => api.put(`/scanner/sources/${id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scanner-sources']);
        queryClient.invalidateQueries(['scanner-status']);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteSourceMutation = useMutation(
    (id) => api.delete(`/scanner/sources/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scanner-sources']);
        queryClient.invalidateQueries(['scanner-status']);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const handleSaveSettings = (e) => {
    e.preventDefault();
    const body = {
      service_url: form.service_url.trim(),
      client_id: form.client_id.trim(),
      schedule_enabled: form.schedule_enabled,
      schedule_cron: form.schedule_cron,
      default_job_status: form.default_job_status,
      title_filter: {
        positive: form.title_positive.split('\n').map((s) => s.trim()).filter(Boolean),
        negative: form.title_negative.split('\n').map((s) => s.trim()).filter(Boolean),
      },
    };
    if (form.api_secret.trim()) body.api_secret = form.api_secret.trim();
    if (form.client_secret.trim()) body.client_secret = form.client_secret.trim();
    saveMutation.mutate(body);
  };

  const handleAddSource = (e) => {
    e.preventDefault();
    if (!newSource.name.trim() || !newSource.careers_url.trim()) {
      void appAlert('Name and careers URL are required');
      return;
    }
    addSourceMutation.mutate({
      name: newSource.name.trim(),
      careers_url: newSource.careers_url.trim(),
      provider: newSource.provider || null,
      enabled: newSource.enabled,
    });
  };

  if (settingsLoading || sourcesLoading) {
    return <div className="loading">Loading scanner configuration…</div>;
  }

  return (
    <div className="settings-section scanner-config">
      <h2>
        <FiGlobe /> Scanner configuration
      </h2>
      <p className="scanner-config-intro">
        GoDash only stores connection credentials and ATS config, then pushes them to the independent
        scanner Docker service (Postgres). Jobs are stored in the scanner — not in GoDash.
        View runs on the{' '}
        <Link to="/job-scanner" className="scanner-inline-link">
          Job scanner <FiExternalLink aria-hidden />
        </Link>{' '}
        page.
      </p>

      <form onSubmit={handleSaveSettings} className="settings-form scanner-config-block">
        <h3><FiKey /> Scanner service connection</h3>
        <p className="scanner-config-intro">
          Same pattern as Custom AI: service URL plus Cloudflare Access client id/secret. Also set the
          shared API secret (<code>SCANNER_SECRET</code> in the container).
        </p>

        <div className="form-group">
          <label>Service URL</label>
          <input
            type="url"
            value={form.service_url}
            onChange={(e) => setForm({ ...form, service_url: e.target.value })}
            placeholder="http://localhost:8790"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>API secret</label>
            <input
              type="password"
              value={form.api_secret}
              onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
              placeholder={secretMeta.api_secret_set ? `Saved (${secretMeta.api_secret_preview})` : 'Required'}
              autoComplete="new-password"
            />
            <small>Sent as <code>X-Scanner-Secret</code>. Leave blank to keep the saved value.</small>
          </div>
          <div className="form-group">
            <label>Cloudflare Access client id</label>
            <input
              type="text"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder="Optional — same as AI Custom"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Cloudflare Access client secret</label>
          <input
            type="password"
            value={form.client_secret}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={
              secretMeta.client_secret_set
                ? `Saved (${secretMeta.client_secret_preview})`
                : 'Optional'
            }
            autoComplete="new-password"
          />
          <small>
            Sent as <code>CF-Access-Client-Id</code> / <code>CF-Access-Client-Secret</code> when set.
            Leave blank to keep the saved secret.
          </small>
        </div>

        <h3><FiClock /> Schedule &amp; filters</h3>
        <p className="scanner-config-intro">
          Pushed to the scanner on save. The scanner runs its own cron from this config.
        </p>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={form.schedule_enabled}
              onChange={(e) => setForm({ ...form, schedule_enabled: e.target.checked })}
            />
            {' '}Enable scheduled scans
          </label>
        </div>

        <div className="form-group">
          <label>Cron expression (UTC)</label>
          <input
            type="text"
            value={form.schedule_cron}
            onChange={(e) => setForm({ ...form, schedule_cron: e.target.value })}
            placeholder="0 6 * * *"
          />
          <small>
            Example daily at 6:00 UTC: <code>0 6 * * *</code>
          </small>
        </div>

        <div className="form-group">
          <label>Default status for new jobs (in scanner DB)</label>
          <select
            value={form.default_job_status}
            onChange={(e) => setForm({ ...form, default_job_status: e.target.value })}
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Title keywords (one per line, at least one must match)</label>
            <textarea
              rows={4}
              value={form.title_positive}
              onChange={(e) => setForm({ ...form, title_positive: e.target.value })}
              placeholder="Engineer&#10;Product Manager"
            />
            <small>Leave empty to include all titles (except negatives)</small>
          </div>
          <div className="form-group">
            <label>Exclude title keywords</label>
            <textarea
              rows={4}
              value={form.title_negative}
              onChange={(e) => setForm({ ...form, title_negative: e.target.value })}
            />
          </div>
        </div>

        <LoadingButton
          type="submit"
          className="btn btn-primary"
          icon={FiSave}
          loading={saveMutation.isLoading}
          loadingLabel="Saving…"
        >
          Save &amp; push to scanner
        </LoadingButton>
      </form>

      <div className="scanner-config-block">
        <h3>Tracked companies (ATS sources)</h3>
        <p className="scanner-config-intro">
          Edited here, then pushed to the scanner service automatically. Use the starter list for
          ~90 popular tech companies on Greenhouse, Ashby, Lever, or Workday.
        </p>
        <div className="scanner-config-seed-row">
          <LoadingButton
            type="button"
            className="btn btn-secondary"
            icon={FiDownload}
            loading={seedTechMutation.isLoading}
            loadingLabel="Seeding…"
            onClick={async () => {
              if (
                await appConfirm(
                  'Add the starter tech company list? Existing companies (same name or URL) are skipped. New ones are enabled and pushed to the scanner.'
                )
              ) {
                seedTechMutation.mutate();
              }
            }}
          >
            Load starter tech companies
          </LoadingButton>
        </div>
        <form onSubmit={handleAddSource} className="settings-form">
          <div className="form-row">
            <div className="form-group">
              <label>Company name</label>
              <input
                value={newSource.name}
                onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                placeholder="Anthropic"
              />
            </div>
            <div className="form-group">
              <label>Careers URL</label>
              <input
                value={newSource.careers_url}
                onChange={(e) => setNewSource({ ...newSource, careers_url: e.target.value })}
                placeholder="https://job-boards.greenhouse.io/anthropic"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Provider</label>
              <select
                value={newSource.provider}
                onChange={(e) => setNewSource({ ...newSource, provider: e.target.value })}
              >
                {SCANNER_PROVIDERS.map((p) => (
                  <option key={p.value || 'auto'} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group scanner-config-add-row">
              <label>
                <input
                  type="checkbox"
                  checked={newSource.enabled}
                  onChange={(e) => setNewSource({ ...newSource, enabled: e.target.checked })}
                />
                {' '}Enabled
              </label>
              <LoadingButton
                type="submit"
                className="btn btn-secondary"
                icon={FiPlus}
                loading={addSourceMutation.isLoading}
              >
                Add source
              </LoadingButton>
            </div>
          </div>
        </form>

        {sources.length === 0 ? (
          <p>No sources configured. Add a company above.</p>
        ) : (
          <table className="data-table scanner-sources-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>URL</th>
                <th>Provider</th>
                <th>Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="scanner-url-cell">
                    <a href={s.careers_url} target="_blank" rel="noreferrer">{s.careers_url}</a>
                  </td>
                  <td>{s.provider || 'auto'}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(s.enabled)}
                      onChange={(e) =>
                        updateSourceMutation.mutate({
                          id: s.id,
                          body: { enabled: e.target.checked },
                        })
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-icon"
                      title="Delete"
                      onClick={async () => {
                        if (await appConfirm(`Delete source "${s.name}"?`)) {
                          deleteSourceMutation.mutate(s.id);
                        }
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ScannerConfig;
